import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { scrollY } = useScroll();
  const location = useLocation();
  
  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const navItems = [
    { name: 'About', href: '/about' },
    { name: 'Leadership Accelerator', href: '/crucible' },
    { name: 'Spark Moments™', href: '/spark-moments' },
    { name: 'LIT Expeditions™', href: '/expeditions' },
    { name: 'Shop', href: 'https://emberimpact.lemonsqueezy.com/', external: true },
    { name: 'Contact', href: '/contact' }
  ];

  return (
    <>
      {/* Minimal CSS for Navigation - Just ensure it stays fixed */}
      <style>{`
        .ember-navigation {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          width: 100vw !important;
          height: auto !important;
          min-height: 80px !important;
          z-index: 999999 !important;
          isolation: isolate !important;
          transform: translateZ(0) !important;
          will-change: transform !important;
        }
        
        /* Ensure hamburger menu is always visible */
        .ember-navigation button[class*="lg:hidden"] {
          background: white !important;
          color: black !important;
          border: 1px solid #ccc !important;
          border-radius: 4px !important;
        }
        
        /* Ensure mobile menu has solid background */
        .ember-navigation .mobile-menu,
        .ember-navigation div[class*="lg:hidden"] {
          background: white !important;
        }
      `}</style>
      
      <nav 
        role="navigation"
        aria-label="Main navigation"
        className={`ember-navigation transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-sm shadow-lg' : 'bg-white/90 backdrop-blur-sm'
        }`}
      >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Brand - Logo + Text */}
          <Link to="/" className="flex items-center group">
            <motion.img 
              src="https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
              alt="EMBER Logo"
              className="w-10 h-10 mr-3 group-hover:scale-105 transition-transform duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            />
            <motion.span 
              className="text-3xl font-bold text-neutral-charcoal group-hover:text-ember-fire transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              EMBER
            </motion.span>
          </Link>

          {/* Desktop Navigation - Dropdown Menu */}
          <div className="hidden lg:flex items-center">
            <div className="relative dropdown-container">
              <motion.button
                className="flex items-center space-x-2 text-lg font-medium text-neutral-dark hover:text-ember-fire transition-colors duration-300 px-4 py-2"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
                aria-controls="desktop-menu"
                aria-label="Open navigation menu"
              >
                <span>Menu</span>
                <ChevronDown className={`w-4 h-4 transform transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </motion.button>
              
              {dropdownOpen && (
                <motion.div
                  id="desktop-menu"
                  role="menu"
                  aria-orientation="vertical"
                  className="absolute top-full right-0 mt-2 w-64 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-neutral-light/50 py-2 z-50"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  {navItems.map((item, index) => (
                    <motion.div
                      key={item.name}
                      role="none"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      {item.external ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          role="menuitem"
                          className={`block px-4 py-3 text-lg font-medium transition-colors duration-300 hover:text-ember-fire hover:bg-ember-glow/10 ${
                            location.pathname === item.href ? 'text-ember-fire bg-ember-glow/10' : 'text-neutral-dark'
                          }`}
                          onClick={() => setDropdownOpen(false)}
                        >
                          {item.name}
                          <span className="sr-only"> (opens in new tab)</span>
                        </a>
                      ) : (
                        <Link
                          to={item.href}
                          role="menuitem"
                          className={`block px-4 py-3 text-lg font-medium transition-colors duration-300 hover:text-ember-fire hover:bg-ember-glow/10 ${
                            location.pathname === item.href ? 'text-ember-fire bg-ember-glow/10' : 'text-neutral-dark'
                          }`}
                          onClick={() => setDropdownOpen(false)}
                        >
                          {item.name}
                        </Link>
                      )}
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="lg:hidden text-black hover:text-ember-fire p-2 bg-white rounded-md"
            onClick={() => setIsOpen(!isOpen)}
            whileTap={{ scale: 0.95 }}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          id="mobile-menu"
          role="menu"
          aria-label="Mobile navigation menu"
          className={`lg:hidden overflow-hidden bg-white shadow-lg ${isOpen ? 'max-h-96' : 'max-h-0'}`}
          animate={{ maxHeight: isOpen ? 384 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="py-4 space-y-3 border-t border-neutral-light bg-white">
            {navItems.map((item, index) => (
              <motion.div
                key={item.name}
                role="none"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: isOpen ? 1 : 0, x: isOpen ? 0 : -20 }}
                transition={{ delay: index * 0.1 }}
              >
                {item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="menuitem"
                    className={`block py-2 text-lg font-medium transition-colors ${
                      location.pathname === item.href ? 'text-ember-fire' : 'text-neutral-dark hover:text-ember-fire'
                    }`}
                  >
                    {item.name}
                    <span className="sr-only"> (opens in new tab)</span>
                  </a>
                ) : (
                  <Link
                    to={item.href}
                    role="menuitem"
                    className={`block py-2 text-lg font-medium transition-colors ${
                      location.pathname === item.href ? 'text-ember-fire' : 'text-neutral-dark hover:text-ember-fire'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </nav>
    </>
  );
};

export default Navigation;